import { forwardRef } from "react";

import { useTheme } from "@mui/material/styles";

import { mergeSx } from "@/common/helpers/mergeSx";
import {
  DashboardHeadlessRichTextInput,
  DashboardHeadlessRichTextInputProps,
} from "@/dashboard-design-system/RichTextInput/DashboardHeadlessRichTextInput";
import { DashboardRichTextContextProvider } from "@/dashboard-design-system/RichTextInput/DashboardRichTextContext";
import { FloatingToolbarPlugin } from "@/dashboard-design-system/RichTextInput/plugins/FloatingToolbarPlugin";

export type DashboardRichTextInputProps = DashboardHeadlessRichTextInputProps & {
  error?: boolean;
};

export const DashboardRichTextInput = forwardRef<HTMLDivElement, DashboardRichTextInputProps>(
  ({ error = false, slotProps, disabled = false, sx, ...rest }, ref) => {
    const theme = useTheme();

    const styles = mergeSx(
      {
        ...(disabled && {
          opacity: 0.5,
        }),
      },
      sx
    );

    const contentStyles = mergeSx(
      {
        position: "relative",
        minHeight: 80,
        padding: theme.spacing(1),
        backgroundColor: theme.palette.surface.neutral.white,
        border: `1px solid ${theme.palette.border.neutral.quiet}`,
        borderRadius: theme.radius(1),
        outlineColor: "transparent",
        transition: "all 50ms ease-out, outline 100ms ease-out",
        "&:focus": {
          border: `1px solid ${theme.palette.border.brand.intense}`,
          outline: `3px solid ${theme.palette.border.brand.moderate}`,
        },
        ...(error && {
          border: `1px solid ${theme.palette.border.danger.intense}`,
        }),
      },
      slotProps?.content?.sx
    );

    const placeholderStyles = mergeSx(
      {
        top: theme.spacing(1),
        left: theme.spacing(1),
      },
      slotProps?.placeholder?.sx
    );

    return (
      <DashboardRichTextContextProvider>
        <DashboardHeadlessRichTextInput
          {...rest}
          ref={ref}
          disabled={disabled}
          slotProps={{
            ...slotProps,
            content: { ...slotProps?.content, sx: contentStyles },
            placeholder: { ...slotProps?.placeholder, sx: placeholderStyles },
          }}
          sx={styles}
        />
        <FloatingToolbarPlugin />
      </DashboardRichTextContextProvider>
    );
  }
);

DashboardRichTextInput.displayName = "DashboardRichTextInput";
